import {groupByUniqueKey} from '@benchling/js-utils';

import {CreateOrUpdateServerProcessOutput} from 'processes/dao/ProcessManagementDAO';

interface UpdateProcessTaskParams {
  id: string;
}

interface BatchSubmitOutputsTaskUpdateManagerParameters<T extends UpdateProcessTaskParams> {
  processOutputs: Array<CreateOrUpdateServerProcessOutput>;
  processTasks?: Array<T>;
  willProcessBatchesInOrder?: boolean;
}

/**
 * This class is used to generate the updates to process tasks that are needed when submitting outputs in batches.
 */
export default class BatchSubmitOutputsTaskUpdateManager<T extends UpdateProcessTaskParams> {
  outputSpecs: Array<CreateOrUpdateServerProcessOutput>;
  taskSpecs: Array<T>;
  taskUpdateParamsByTaskId: Record<string, T>;
  sortedMaxOutputRowIdxByTaskId: Array<[string, number]>;
  willProcessBatchesInOrder: boolean;

  constructor({
    processTasks,
    processOutputs,
    willProcessBatchesInOrder = false,
  }: BatchSubmitOutputsTaskUpdateManagerParameters<T>) {
    this.outputSpecs = processOutputs;
    this.taskSpecs = processTasks ?? [];
    this.willProcessBatchesInOrder = willProcessBatchesInOrder;

    this.taskUpdateParamsByTaskId = groupByUniqueKey(this.taskSpecs, (spec) => spec.id);

    const maxOutputRowIdxByTaskId = new Map<string, number>();
    for (let i = 0; i < this.outputSpecs.length; i++) {
      const outputSubmitParam = this.outputSpecs[i];
      if (outputSubmitParam.processTaskId != null) {
        maxOutputRowIdxByTaskId.set(outputSubmitParam.processTaskId, i);
      }
    }
    this.sortedMaxOutputRowIdxByTaskId = Array.from(maxOutputRowIdxByTaskId.entries()).sort(
      ([leftTaskId, leftMaxIndex], [rightTaskId, rightMaxIndex]) => leftMaxIndex - rightMaxIndex
    );
  }

  getTaskUpdatesForBatch({start, end}: {start: number; end: number}): Array<T> {
    const taskUpdates = [];

    let idx = 0;

    const iterate = (): void => {
      if (this.willProcessBatchesInOrder) {
        this.sortedMaxOutputRowIdxByTaskId.shift();
      } else {
        idx++;
      }
    };

    while (idx < this.sortedMaxOutputRowIdxByTaskId.length) {
      const [taskId, maxOutputRowIdx] = this.sortedMaxOutputRowIdxByTaskId[0];
      if (maxOutputRowIdx < start) {
        iterate();
        continue;
      } else if (maxOutputRowIdx >= end) {
        break;
      } else if (this.taskUpdateParamsByTaskId[taskId] != null) {
        taskUpdates.push(this.taskUpdateParamsByTaskId[taskId]);
      }
      iterate();
    }

    return taskUpdates;
  }
}
